/**
 * "Who's coming" — the invite form and the guest list.
 *
 * Mounted on the itinerary page and the booking page. It renders NOTHING on a
 * solo trip: the brief is that people may only be added to a trip that is not
 * solo, and a disabled control that explains why it is disabled is still an
 * invitation to argue with it. The one exception is a trip that has already
 * been shared and has since been changed to solo — those guests are shown, so
 * the owner can see and revoke what is still out there.
 *
 * The owner never leaves this screen to invite someone. Copying a link is one
 * tap, and the link is per person so the row and the link are the same thing.
 */

import React, { useCallback, useEffect, useState } from 'react';

import Alert from '@mui/material/Alert';
import Avatar from '@mui/material/Avatar';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import Paper from '@mui/material/Paper';
import Snackbar from '@mui/material/Snackbar';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';

import ContentCopyRoundedIcon from '@mui/icons-material/ContentCopyRounded';
import GroupAddRoundedIcon from '@mui/icons-material/GroupAddRounded';
import PersonRemoveRoundedIcon from '@mui/icons-material/PersonRemoveRounded';

import {
  MAX_COMPANIONS,
  inviteLink,
  requireInviteEmail,
  summariseGuests,
} from '../../../utils/tripSharing';
import {
  findShareForTrip,
  inviteCompanion,
  listCompanions,
  revokeCompanion,
  stopSharing,
  upsertShare,
} from '../../../utils/tripInvites';

function initialsOf(name) {
  return String(name || '?')
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0].toUpperCase())
    .join('');
}

/**
 * @param {object}   props
 * @param {string}   props.ownerUid       the signed-in traveller
 * @param {string}   props.tripId
 * @param {'ITINERARY'|'BOOKING'} props.tripType
 * @param {boolean}  props.shareable      false on a solo trip
 * @param {string?}  props.blockedReason  why not, shown only when guests exist
 * @param {Function} props.buildSnapshot  () => snapshot, called on every publish
 * @param {Function} props.onShareReady   (share, invites) => void, so the page can
 *                                        show the thread and send final copies
 */
export default function TripCompanions({
  ownerUid,
  tripId,
  tripType,
  shareable,
  blockedReason,
  buildSnapshot,
  onShareReady,
}) {
  const [invites, setInvites] = useState([]);
  const [share, setShare] = useState(null);
  const [loading, setLoading] = useState(true);
  // Distinguishes "this trip has never been shared" from "we could not find
  // out". Acting on the first when the truth is the second mints a second
  // share and strands every link already sent, so the invite button is held
  // shut until the load has actually succeeded.
  const [loadFailed, setLoadFailed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');

  const live = invites.filter((i) => !i.revoked);

  // Load the existing share and guest list once. Both are owner-scoped reads;
  // a trip that has never been shared simply has neither.
  useEffect(() => {
    let cancelled = false;

    (async () => {
      if (!ownerUid || !tripId) {
        setLoading(false);
        return;
      }
      try {
        const [foundShare, list] = await Promise.all([
          findShareForTrip({ ownerUid, tripId }),
          listCompanions({ ownerUid, tripId }),
        ]);
        if (cancelled) return;
        setShare(foundShare || null);
        setInvites(list);
        setLoadFailed(false);
        if (foundShare) onShareReady?.(foundShare, list);
      } catch (e) {
        if (cancelled) return;
        console.error('TripCompanions: could not load sharing state', e);
        setLoadFailed(true);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
    // onShareReady is redefined by the parent on every render; listing it here
    // would reload the guest list continuously.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ownerUid, tripId]);

  const handleInvite = useCallback(async () => {
    setError('');

    if (!name.trim()) {
      setError('Add a name so you can tell your guest list apart.');
      return;
    }
    // Required now, not optional. The address is what the database checks a
    // reader against, so an invitation without one could never be opened.
    const emailCheck = requireInviteEmail(email);
    if (emailCheck.error) {
      setError(emailCheck.error);
      return;
    }
    if (live.length >= MAX_COMPANIONS) {
      setError(`You can invite up to ${MAX_COMPANIONS} people to one trip.`);
      return;
    }

    setBusy(true);
    try {
      const snapshot = buildSnapshot();

      // Publish (or refresh) the owner's record first. An invitation whose
      // link resolves to nothing is worse than no invitation.
      const savedShare = await upsertShare({
        ownerUid,
        snapshot,
        existingShareId: share?.shareId || null,
        threadId: share?.threadId || null,
      });

      const invite = await inviteCompanion({
        shareId: savedShare.shareId,
        threadId: savedShare.threadId,
        ownerUid,
        tripType,
        tripId,
        name,
        email,
        // Carried on the invite itself, so the link works the instant it is
        // copied rather than after the next publish.
        snapshot,
        currentCount: live.length,
        existingEmails: live.map((i) => i.email).filter(Boolean),
      });

      const next = [invite, ...invites];
      setShare(savedShare);
      setInvites(next);
      setName('');
      setEmail('');
      onShareReady?.(savedShare, next);

      setNotice(
        `${invite.name} has been added. Send them their link — they will need to sign in as ${invite.email} to open it.`
      );
    } catch (e) {
      setError(e.message || 'That did not save. Check your connection and try again.');
    } finally {
      setBusy(false);
    }
  }, [name, email, live.length, ownerUid, share, tripId, tripType, buildSnapshot, invites, onShareReady]);

  const handleCopy = useCallback(async (invite) => {
    const link = inviteLink(invite.inviteId);
    try {
      await navigator.clipboard.writeText(link);
      setNotice(`Link for ${invite.name} copied. Only ${invite.email} can open it.`);
    } catch {
      // Clipboard access is refused outside a secure context and in some
      // in-app browsers. Showing the link is a worse experience than copying
      // it, and a much better one than a silent no-op.
      setNotice(link);
    }
  }, []);

  const handleRevoke = useCallback(
    async (invite) => {
      setError('');
      setBusy(true);
      try {
        // The whole invite, not just its id: revoking also takes the address
        // off the conversation, and that needs the thread and the email.
        await revokeCompanion(invite);
        const next = invites.map((i) =>
          i.inviteId === invite.inviteId ? { ...i, revoked: true, snapshot: null } : i
        );
        setInvites(next);
        onShareReady?.(share, next);
        setNotice(`${invite.name} can no longer see this trip.`);
      } catch {
        setError('That did not save. Check your connection and try again.');
      } finally {
        setBusy(false);
      }
    },
    [invites, share, onShareReady]
  );

  const handleStopSharing = useCallback(async () => {
    setError('');
    setBusy(true);
    try {
      await stopSharing({ shareId: share?.shareId, threadId: share?.threadId, invites });
      const next = invites.map((i) => ({ ...i, revoked: true, snapshot: null }));
      setInvites(next);
      onShareReady?.(share, next);
      setNotice('Sharing is off. Nobody can open this trip any more.');
    } catch {
      setError('That did not save. Check your connection and try again.');
    } finally {
      setBusy(false);
    }
  }, [share, invites, onShareReady]);

  // A solo trip with nobody invited has nothing to say. A solo trip that was
  // shared BEFORE it became solo still shows its guests, so they can be
  // revoked — hiding live links because a dropdown changed would be worse.
  if (!shareable && live.length === 0) return null;

  return (
    <Paper elevation={1} sx={{ p: 2, m: 2 }}>
      <Typography
        sx={{ display: 'flex', alignItems: 'center', fontWeight: 600, color: 'text.primary', mb: 0.5 }}
      >
        <GroupAddRoundedIcon sx={{ fontSize: '1.1rem', mr: 0.75, color: 'text.secondary' }} />
        Who&apos;s coming
      </Typography>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Invite the people travelling with you. They will need a Vhakacha account with the address you
        give here — the link only works for them. They can read the trip and leave comments, but only
        you can change it or finalise it.
      </Typography>

      {!shareable && blockedReason && (
        <Alert severity="info" sx={{ mb: 2 }}>
          {blockedReason} The people below can still see what you have already shared.
        </Alert>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {loadFailed && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          We could not load your guest list just now, so inviting is paused. Reload the page to try
          again — carrying on from here could send out a link that stops working.
        </Alert>
      )}

      {shareable && !loadFailed && (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, alignItems: 'flex-start', mb: 2 }}>
          <TextField
            id="companion-name"
            label="Name"
            size="small"
            value={name}
            onChange={(e) => setName(e.target.value)}
            disabled={busy}
            sx={{ flex: '1 1 160px' }}
          />
          <TextField
            id="companion-email"
            label="Email"
            required
            size="small"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            disabled={busy}
            helperText="They sign in with this to open the trip"
            sx={{ flex: '1 1 240px' }}
          />
          <Button
            variant="contained"
            onClick={handleInvite}
            disabled={busy || loading || loadFailed || live.length >= MAX_COMPANIONS}
            sx={{ mt: 0.5 }}
          >
            {busy ? <CircularProgress size={18} color="inherit" /> : 'Invite'}
          </Button>
        </Box>
      )}

      <Divider sx={{ mb: 1.5 }} />

      {loading ? (
        <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>
          Loading your guest list…
        </Typography>
      ) : invites.length === 0 ? (
        <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>
          Nobody invited yet. Add someone above and share their link.
        </Typography>
      ) : (
        <>
          <Typography variant="caption" color="text.secondary">
            {summariseGuests(invites)}
          </Typography>

          <Box component="ul" sx={{ listStyle: 'none', p: 0, m: 0, mt: 1 }}>
            {invites.map((invite) => (
              <Box
                component="li"
                key={invite.inviteId}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.5,
                  py: 1,
                  opacity: invite.revoked ? 0.5 : 1,
                }}
              >
                <Avatar sx={{ width: 34, height: 34, fontSize: '0.8rem' }} variant="rounded">
                  {initialsOf(invite.name)}
                </Avatar>

                <Box sx={{ minWidth: 0, flex: 1 }}>
                  <Typography noWrap sx={{ fontWeight: 600 }}>
                    {invite.name}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" noWrap component="div">
                    {invite.email}
                  </Typography>
                </Box>

                {invite.revoked ? (
                  <Chip size="small" label="Removed" />
                ) : (
                  <Chip
                    size="small"
                    variant="outlined"
                    color={invite.status === 'VIEWED' ? 'success' : 'default'}
                    label={invite.status === 'VIEWED' ? 'Viewed' : 'Invited'}
                  />
                )}

                {!invite.revoked && (
                  <>
                    <Tooltip title="Copy invite link">
                      <IconButton
                        size="small"
                        aria-label={`Copy invite link for ${invite.name}`}
                        onClick={() => handleCopy(invite)}
                      >
                        <ContentCopyRoundedIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Remove from trip">
                      <IconButton
                        size="small"
                        aria-label={`Remove ${invite.name} from this trip`}
                        onClick={() => handleRevoke(invite)}
                        disabled={busy}
                      >
                        <PersonRemoveRoundedIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </>
                )}
              </Box>
            ))}
          </Box>
        </>
      )}

      {live.length > 0 && (
        <>
          <Divider sx={{ my: 1.5 }} />
          <Button
            size="small"
            color="error"
            onClick={handleStopSharing}
            disabled={busy}
          >
            Stop sharing this trip
          </Button>
          <Typography variant="caption" color="text.secondary" component="div">
            Every link stops working immediately. You can invite people again afterwards.
          </Typography>
        </>
      )}

      <Snackbar
        open={!!notice}
        autoHideDuration={5000}
        onClose={() => setNotice('')}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="info" onClose={() => setNotice('')} sx={{ width: '100%' }}>
          {notice}
        </Alert>
      </Snackbar>
    </Paper>
  );
}
